// @flow
import type { ChildrenArray, Element } from 'react';
import React from 'react';

import {
  productColor,
  productColorSunflower,
  illustrationColor,
} from '../../designSystem.js';
import { Text } from '../../Text.js';
import Latex from '../../Latex.js';
import { LatexTextAtom } from '../../LatexTextAtom.js';
import { List } from '../../List.js';
import Image from '../../Image.js';
import BoxAndWhiskerPlot from '../../BoxAndWhiskerPlot.js';
import { CartesianPlane } from '../../CartesianPlane.js';
import { NumberLine } from '../../NumberLine.js';
import { StemAndLeafPlot } from '../../StemAndLeafPlot.js';
import TabularData from '../../TabularData.js';
import { TwoColumnProof } from '../../TwoColumnProof.js';
import { AlgebraicWorking } from '../../AlgebraicWorking.js';
import { PseudoCodeBlock } from '../../PseudoCodeBlock.js';
import { RSLAnnotatedFigure } from '../common-components/RSLAnnotatedFigure.js';
import { RSLFigureGrid } from '../common-components/RSLFigureGrid.js';
import { RSLTechnologyToolScreenshot } from '../common-components/RSLTechnologyToolScreenshot.js';
import DraftImage from '../../DraftImage.js';

import {
  DisabilityIcon,
  EnglishLanguageLearnerIcon,
  InstructionalSupportInformationIcon,
  CommonMisconceptionIcon,
} from '../../InstructionalSupportIcons.js';
import { childrenArrayMatcher } from '../../childrenArrayMatcher.js';
import {
  types as t,
  usePropsValidation,
} from '../../validation/runtime-checks/index.js';
import type { CompilerInjectedProps } from '../../validation/runtime-checks/index.js';

export type RSLTNInstructionalSupportsChildren = ChildrenArray<
  | Element<typeof RSLTNInstructionalSupportInformation>
  | Element<typeof RSLTNEnglishLearnerSupport>
  | Element<typeof RSLTNInstructionalSupportDisability>
  | Element<typeof RSLTNCommonMisconceptions>,
>;

export const RSLInstructionalSupportsChildrenChecker = childrenArrayMatcher(
  {
    min: 0,
    max: 1,
    elementType: t.elementType(RSLTNInstructionalSupportInformation),
  },
  {
    min: 0,
    max: 1,
    elementType: t.elementType(RSLTNEnglishLearnerSupport),
  },
  {
    min: 0,
    max: 1,
    elementType: t.elementType(RSLTNInstructionalSupportDisability),
  },
  {
    min: 0,
    max: 1,
    elementType: t.elementType(RSLTNCommonMisconceptions),
  },
);

export function RSLTNInstructionalSupports(props: {
  children: RSLTNInstructionalSupportsChildren,
  ...CompilerInjectedProps,
}) {
  const { failedValidation } = usePropsValidation(
    props,
    RSLTNInstructionalSupports,
  );

  if (failedValidation) return null;
  return (
    <div style={{ marginTop: 25, marginBottom: 25 }}>{props.children}</div>
  );
}
RSLTNInstructionalSupports.spec = {
  children: RSLInstructionalSupportsChildrenChecker,
};

type RSLTNInstructionalSupportChildren = ChildrenArray<
  // Primitives
  | Element<typeof Text>
  | Element<typeof Latex>
  | Element<typeof LatexTextAtom>
  | Element<typeof List>
  | Element<typeof Image>
  | Element<typeof DraftImage>
  // probably only need those above this line
  | Element<typeof BoxAndWhiskerPlot>
  | Element<typeof CartesianPlane>
  | Element<typeof NumberLine>
  | Element<typeof StemAndLeafPlot>
  | Element<typeof TabularData>
  | Element<typeof TwoColumnProof>
  | Element<typeof AlgebraicWorking>
  | Element<typeof PseudoCodeBlock>
  // components designed for RSL
  | Element<typeof RSLAnnotatedFigure>
  | Element<typeof RSLFigureGrid>
  | Element<typeof RSLTechnologyToolScreenshot>,
>;

const RSLInstructionalSupportChildrenChecker = t.childrenArray(
  t.unionOfTypes(
    // Primitives
    t.elementType(Text),
    t.elementType(Latex),
    t.elementType(LatexTextAtom),
    t.elementType(List),
    t.elementType(Image),
    t.elementType(DraftImage),
    // again, probably only need those above this line
    t.elementType(BoxAndWhiskerPlot),
    t.elementType(CartesianPlane),
    t.elementType(NumberLine),
    t.elementType(StemAndLeafPlot),
    t.elementType(TabularData),
    t.elementType(TwoColumnProof),
    t.elementType(AlgebraicWorking),
    t.elementType(PseudoCodeBlock),
    // components designed for RSL
    t.elementType(RSLAnnotatedFigure),
    t.elementType(RSLFigureGrid),
    t.elementType(RSLTechnologyToolScreenshot),
  ),
);

const RSLInstructionalSupportSpec = {
  heading: t.string,
  children: RSLInstructionalSupportChildrenChecker,
};

export function RSLTNInstructionalSupportInformation(props: {
  heading: string,
  _isLessonSupport?: boolean,
  children: RSLTNInstructionalSupportChildren,
  ...CompilerInjectedProps,
}) {
  const { failedValidation } = usePropsValidation(
    props,
    RSLTNInstructionalSupportInformation,
  );

  if (failedValidation) return null;
  return (
    <RSLTNCollapsibleInternal
      title="Targeted instructional strategies"
      icon={<InstructionalSupportInformationIcon />}
      heading={props.heading}
      isLessonSupport={props._isLessonSupport ?? false}
    >
      {props.children}
    </RSLTNCollapsibleInternal>
  );
}
RSLTNInstructionalSupportInformation.spec = RSLInstructionalSupportSpec;

export function RSLTNEnglishLearnerSupport(props: {
  heading: string,
  _isLessonSupport?: boolean,
  children: RSLTNInstructionalSupportChildren,
  ...CompilerInjectedProps,
}) {
  const { failedValidation } = usePropsValidation(
    props,
    RSLTNEnglishLearnerSupport,
  );

  if (failedValidation) return null;
  return (
    <RSLTNCollapsibleInternal
      title="English language learner support"
      icon={<EnglishLanguageLearnerIcon />}
      heading={props.heading}
      isLessonSupport={props._isLessonSupport ?? false}
    >
      {props.children}
    </RSLTNCollapsibleInternal>
  );
}
RSLTNEnglishLearnerSupport.spec = RSLInstructionalSupportSpec;

export function RSLTNInstructionalSupportDisability(props: {
  heading: string,
  _isLessonSupport?: boolean,
  children: RSLTNInstructionalSupportChildren,
  ...CompilerInjectedProps,
}) {
  const { failedValidation } = usePropsValidation(
    props,
    RSLTNInstructionalSupportDisability,
  );

  if (failedValidation) return null;
  return (
    <RSLTNCollapsibleInternal
      title="Student with disabilities support"
      icon={<DisabilityIcon />}
      heading={props.heading}
      isLessonSupport={props._isLessonSupport ?? false}
    >
      {props.children}
    </RSLTNCollapsibleInternal>
  );
}
RSLTNInstructionalSupportDisability.spec = RSLInstructionalSupportSpec;

export function RSLTNCommonMisconceptions(props: {|
  heading: string,
  _isLessonSupport?: boolean,
  children: RSLTNInstructionalSupportChildren,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    RSLTNCommonMisconceptions,
  );

  if (failedValidation) return null;
  return (
    <RSLTNCollapsibleInternal
      title="Address student misconceptions"
      icon={<CommonMisconceptionIcon />}
      heading={props.heading}
      isLessonSupport={props._isLessonSupport ?? false}
    >
      {props.children}
    </RSLTNCollapsibleInternal>
  );
}
RSLTNCommonMisconceptions.spec = RSLInstructionalSupportSpec;

// TODO there seems to be some additional complexity
// in the old collspsible we can remove here.
const RSLTNCollapsibleInternal = (props: {
  openByDefault?: boolean,
  title: string,
  icon:
    | Element<typeof DisabilityIcon>
    | Element<typeof EnglishLanguageLearnerIcon>
    | Element<typeof CommonMisconceptionIcon>
    | Element<typeof InstructionalSupportInformationIcon>,
  heading: string,
  isLessonSupport: boolean,
  children: React$Node,
}) => {
  const [_open, setOpen] = React.useState(props.openByDefault || false);
  const open = _open;
  return (
    <div
      style={{
        border: props.isLessonSupport
          ? 'none'
          : `2px solid ${productColor.iron}`,
        padding: 1,
        borderRadius: 8,
        marginBottom: 16,
        backgroundColor: props.isLessonSupport
          ? illustrationColor.seashell
          : 'auto',
      }}
    >
      <div style={{ breakInside: 'avoid' }}>
        <div
          style={{
            display: 'table',
            height: 33,
            borderBottom: '1px solid rgba(153, 164, 175, 0.3)',
            cursor: 'pointer',
            marginBottom: -16,
            border: 'none',
            padding: 16,
          }}
          onClick={React.useCallback(() => setOpen(!open), [open])}
        >
          <div
            style={{
              display: 'table-cell',
              verticalAlign: 'middle',
              width: '100%',
            }}
          >
            <span style={{ display: 'table-cell', verticalAlign: 'middle' }}>
              {props.icon}{' '}
            </span>
            <div style={{ display: 'table-cell', verticalAlign: 'middle' }}>
              <h1
                style={{
                  fontFamily: 'Proxima Nova',
                  fontStyle: 'normal',
                  fontWeight: 600,
                  fontSize: 16,
                  margin: 0,
                  color: '#34495e',
                  width: '100%',
                  display: 'table-cell',
                  verticalAlign: 'middle',
                }}
              >
                {props.heading}
              </h1>
              <p
                style={{
                  color: productColorSunflower.grey90,
                  fontSize: 16,
                  margin: 0,
                }}
              >
                {props.title}
              </p>
            </div>
          </div>
          <div style={{ display: 'table-cell', verticalAlign: 'middle' }}>
            {React.createElement(HollowCollapseChevron, { isOpen: open })}
          </div>
        </div>
        <div
          style={{
            // When we are closed we still want children to be in the DOM
            // AND (critically) we want the children to exist in the box tree
            // as well, so that we can perform accurate layout measurements.
            // This combination of styles ensures children exist in the box
            // tree, but do not affect layout, nor are drawn to the screen.
            visibility: open ? 'visible' : 'hidden',
            height: open ? 'auto' : 0,
            marginBottom: open ? 16 : 'auto',
            fontfamily: 'Proxima Nova',
            fontSize: 16,
            lineHeight: '22px',
            fontWeight: 400,
            padding: '0px 20px',
            marginTop: 16,
          }}
        >
          {props.children}
        </div>
      </div>
    </div>
  );
};

const HollowCollapseChevron = ({ isOpen }: { isOpen: boolean }) => (
  <svg
    width="16"
    height="10"
    viewBox="0 0 16 10"
    fill="none"
    style={{ transform: isOpen ? `rotate(180deg)` : undefined }}
  >
    <path
      d="M0.159847 2.36855L7.63143 9.83962C7.73818 9.94648 7.86099 10 7.99992 10C8.13884 10 8.26188 9.94648 8.36869 9.83962L15.8397 2.36855C15.9467 2.26151 16 2.13871 16 1.99972C16 1.8608 15.9465 1.73782 15.8397 1.63101L15.0382 0.829631C14.9314 0.722769 14.8084 0.669366 14.6694 0.669366C14.5305 0.669366 14.4075 0.722769 14.3007 0.829631L7.99992 7.13028L1.69893 0.829237C1.59212 0.722375 1.46909 0.66931 1.33039 0.66931C1.19124 0.66931 1.06826 0.722713 0.961452 0.829237L0.159904 1.63096C0.0530977 1.73776 3.05176e-05 1.8608 3.05176e-05 1.99967C-2.47955e-05 2.1387 0.0530415 2.26168 0.159847 2.36855Z"
      fill={productColorSunflower.grey}
    />
  </svg>
);
